import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useFormDataStore } from "../store/formDataStore";
import {
  buildNarrativeIncident,
  getEvidenceData,
  isNarrativeOnly,
} from "@/features/reports/api/reportApi";
import {
  incidentDataQuery,
  reportMetadataQuery,
  reportNarrativeQuery,
} from "../../../features/reports/api/reportQueries";
import { useResolvedReportSchema } from "../../../features/reports/hooks/useResolvedReportSchema";
import { getTranscript } from "../../../shared/api/transcriptsApi";
import { VideoData, VideoStatus } from "../../../shared/interfaces/VideoData";
import { useToast } from "../../../shared/toast";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { coReviewKeys } from "@/features/co-review/api/coReviewQueries";
import { useCoComments } from "@/features/co-review/hooks/useCoComments";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";

// Exported so other pages (e.g. FinalEdits' deep-link gate) gate on the
// exact same list — divergence causes redirect loops.
export const EDITABLE_STATUSES = [
  ReportStatus.PENDING_REVIEW,
  ReportStatus.CHANGES_REQUESTED,
];

/**
 * The editor's data layer, on react-query (metadata + incident queries, the
 * shared schema-resolution hook) with the side effects kept imperative. The
 * returned API is unchanged — the values below stay local state because the
 * WebSocket layer mutates them through the setters (video frames, reportType
 * pushes, showRMSCommit updates).
 *
 * Ordering contract (the old Promise.allSettled serialization, preserved):
 * the metadata effect decides the non-editable redirect BEFORE the incident
 * effect may initStore — the incident effect waits for metadata success and
 * checks the redirect flag; effects within one commit run in declaration
 * order.
 */
export const useReportData = () => {
  const { reportID } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const coApprovalEnabled = useCoApprovalEnabled();

  const [videoData, setVideoData] = useState<VideoData | VideoData[] | null>(
    null,
  );
  const [videoLoadStatus, setVideoLoadStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [showRMSCommit, setShowRMSCommit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportType, setReportType] = useState<string>("");
  const [lastModifiedTime, setLastModifiedTime] = useState<Date | null>(null);
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [rms, setRms] = useState<string>("");
  const [reportStatus, setReportStatus] = useState<string>("");
  const [coReturnedAt, setCoReturnedAt] = useState<string | null>(null);
  const [reportOwnerUserId, setReportOwnerUserId] = useState<string | null>(null);
  // Set once the form store has been initialized (from the artifact OR the
  // narrative) — drives the loading gate in narrative-only mode, where no
  // schema ever arrives.
  const [incidentReady, setIncidentReady] = useState<boolean>(false);

  // True once the non-editable redirect fired — nothing below may touch the
  // form store afterwards.
  const redirectedRef = useRef(false);
  // First incident arrival inits the store (clearing undo history); later
  // arrivals (the narrativeUpdated refetch) setFormData, preserving undo.
  const incidentInitializedRef = useRef(false);
  // Which body shape seeded the store. Used to detect the narrative-only→full
  // flip: on that transition we re-init (clearing undo) instead of setFormData
  // so the user can't Undo back into the incomplete narrative-only state.
  const initializedSourceRef = useRef<"narrative" | "full" | null>(null);
  // The lock toast fires once per mount (the old code only toasted on the
  // initial fetch, never on refetches).
  const lockToastShownRef = useRef(false);
  // Evidence fan-out runs once per mount (the old reFetch skipped it).
  const evidenceFetchedRef = useRef(false);

  const metadataQuery = useQuery({
    ...reportMetadataQuery(reportID ?? ""),
    enabled: !!reportID,
  });
  // Narrative-only mode is server-driven (metadata flag). Derived (not state)
  // so it's available synchronously to gate WHICH body endpoint we fetch:
  // narrative-only reports read the plain-text narrative from /report-narrative;
  // full reports read the schema-bound artifact from /artifact.
  const metadata = metadataQuery.data;
  const narrativeOnly = metadata ? isNarrativeOnly(metadata) : false;

  const incidentQuery = useQuery({
    ...incidentDataQuery(reportID ?? ""),
    enabled: !!reportID && !!metadata && !narrativeOnly,
  });
  const narrativeQuery = useQuery({
    ...reportNarrativeQuery(reportID ?? ""),
    enabled: !!reportID && !!metadata && narrativeOnly,
  });

  // (1) Metadata: redirect gate + derived review state.
  useEffect(() => {
    if (!metadata || !reportID) return;

    if (!EDITABLE_STATUSES.includes(metadata.reportStatus as ReportStatus)) {
      redirectedRef.current = true;
      navigate(`/preview-report-info/${reportID}`);
      return;
    }

    setReportStatus(metadata.reportStatus);
    setShowRMSCommit(![null, "", "KAR"].includes(metadata.rms));
    setEnableEditing(metadata.canEdit);
    setRms(metadata.rms || "KAR");
    setCoReturnedAt(
      metadata.reportStatus === ReportStatus.CHANGES_REQUESTED
        ? (metadata.reviewedAt ?? metadata.updatedAt ?? null)
        : null,
    );
    setReportOwnerUserId(metadata.userId);

    if (!metadata.canEdit && !lockToastShownRef.current) {
      lockToastShownRef.current = true;
      toast.warning(
        "This report is currently locked for editing because another user generated it. You can view and freely navigate through all sections, but editing is disabled. If you believe you should have access, please contact your system administrator.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, reportID, navigate]);

  // (2) Incident: store init on first arrival, setFormData on refetches.
  // Gated on metadata success so the redirect decision always runs first.
  const incidentData = incidentQuery.data;
  const incidentUpdatedAt = incidentQuery.dataUpdatedAt;
  useEffect(() => {
    if (!incidentData || !metadataQuery.isSuccess || redirectedRef.current) {
      return;
    }
    setLastModifiedTime(
      incidentData.updatedAt ? new Date(incidentData.updatedAt) : new Date(),
    );
    setReportType(incidentData.reportType);
    const store = useFormDataStore.getState();
    // Re-init (clearing undo) on first arrival OR on the narrative-only→full
    // flip — the full artifact must not be undoable back into the narrative.
    if (
      !incidentInitializedRef.current ||
      initializedSourceRef.current === "narrative"
    ) {
      incidentInitializedRef.current = true;
      initializedSourceRef.current = "full";
      store.initStore(incidentData.reportContent, 0);
    } else {
      store.setFormData(incidentData.reportContent);
    }
    setIncidentReady(true);
  }, [incidentData, incidentUpdatedAt, metadataQuery.isSuccess]);

  // (2b) Narrative-only: the report has no schema-bound artifact, so build a
  // minimal incident model from the plain-text narrative and seed the store.
  // NarrativeOnlyEditor reads/writes Narratives[0] under the RMS-appropriate
  // key (eForce → Text, KAR → Narrative). Shares incidentInitializedRef with
  // the incident effect so that, after the full report is generated and the
  // editor flips to full mode, the artifact replaces this via setFormData.
  const narrativeData = narrativeQuery.data;
  const narrativeUpdatedAt = narrativeQuery.dataUpdatedAt;
  useEffect(() => {
    // Guard on narrativeOnly so a stale narrative payload can never overwrite
    // a full report after the mode flips false (post full-report generation).
    if (
      !narrativeOnly ||
      !narrativeData ||
      !metadataQuery.isSuccess ||
      redirectedRef.current
    ) {
      return;
    }
    setLastModifiedTime(
      narrativeData.updatedAt ? new Date(narrativeData.updatedAt) : new Date(),
    );
    if (narrativeData.reportType) setReportType(narrativeData.reportType);
    const reportContent = buildNarrativeIncident(
      narrativeData.narrative,
      metadata?.rms ?? null,
    );
    const store = useFormDataStore.getState();
    if (!incidentInitializedRef.current) {
      incidentInitializedRef.current = true;
      initializedSourceRef.current = "narrative";
      store.initStore(reportContent, 0);
    } else {
      store.setFormData(reportContent);
    }
    setIncidentReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrativeData, narrativeUpdatedAt, metadataQuery.isSuccess]);

  // (3) Evidence fan-out: once per mount, off the metadata's evidence ids.
  const evidenceIds = metadata?.evidenceIds;
  useEffect(() => {
    if (!evidenceIds || redirectedRef.current || evidenceFetchedRef.current) {
      return;
    }
    evidenceFetchedRef.current = true;
    let cancelled = false;
    Promise.all(evidenceIds.map((id: string) => getEvidenceData(id)))
      .then(async (evidenceResults) => {
        if (cancelled) return;
        // `/evidence/details` returns player metadata only — it never includes
        // the transcript body. Hydrate it here for already-transcribed videos so
        // VideoPreview's overlay + Edit/Download UI (gated on `transcript`)
        // render. Mirrors GenerateReport's useVideoData.
        await Promise.allSettled(
          evidenceResults.map(async (video) => {
            if (video.status === VideoStatus.TRANSCRIPT_READY) {
              try {
                const { transcript } = await getTranscript(video.id);
                video.transcript = transcript;
              } catch {
                // Leave the transcript empty on failure; the overlay simply
                // won't render for this video and playback is unaffected.
              }
            }
          }),
        );
        if (cancelled) return;
        setVideoData(evidenceResults);
        setVideoLoadStatus("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching evidence data:", err);
        setVideoLoadStatus("error");
        toast.error(
          "An error occurred while fetching evidence data. Please try again later.",
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceIds]);

  // (4) CO comments: read from the shared query cache ("current" cycle),
  // mutated through the co-review feature's optimistic mutations. The status
  // gate doubles as the redirect gate — reportStatus is only ever set after
  // the metadata effect decided against redirecting. useCoComments yields []
  // whenever disabled or errored (the disabled-observer and error-retains-
  // data traps live there, not here).
  const coCommentsEnabled =
    !!reportID &&
    coApprovalEnabled &&
    reportStatus === ReportStatus.CHANGES_REQUESTED;
  const { comments: coComments, error: coCommentsError } = useCoComments({
    reportId: reportID,
    cycle: "current",
    enabled: coCommentsEnabled,
  });

  // Refresh alongside every metadata arrival (the old code fetched them on
  // both the initial load and reFetch).
  const metadataUpdatedAt = metadataQuery.dataUpdatedAt;
  useEffect(() => {
    if (!reportID || !metadataUpdatedAt) return;
    queryClient.invalidateQueries({
      queryKey: coReviewKeys.comments(reportID),
    });
  }, [reportID, metadataUpdatedAt, queryClient]);

  useEffect(() => {
    if (!coCommentsError) return;
    console.error("Error fetching CO comments:", coCommentsError);
    toast.error(
      "An error occurred while loading Commanding Officer review comments. Please refresh to try again.",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coCommentsError]);

  // (5) Fatal errors: destroy the store only when the initial load failed
  // (a failed refetch toasts and bails to home but keeps the store, exactly
  // like the old reFetch catch).
  const fatalError =
    metadataQuery.error || incidentQuery.error || narrativeQuery.error;
  useEffect(() => {
    if (!fatalError) return;
    // The non-editable preview redirect takes precedence: when metadata
    // already bounced the user, a parallel incident-fetch failure must not
    // override it with a home redirect + error toast (old serialized
    // ordering).
    if (redirectedRef.current) return;
    if (!incidentInitializedRef.current) {
      useFormDataStore.getState().destroyStore();
    }
    toast.error(
      (isAxiosError(fatalError) && fatalError.response?.data?.error) ||
        "An error occurred while fetching extracted incident information. Please try again later.",
    );
    navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fatalError]);

  // Schema: the shared editor-parity pipeline (full resolve + oneOf→enum).
  // Skipped entirely in narrative-only mode — there's no schema form to render.
  const schemaHook = useResolvedReportSchema({
    enabled: !!reportType && !narrativeOnly,
    reportType,
    rms: rms || null,
    incident: null,
    karSlice: false,
    oneOfToEnum: true,
    refFilter: "all",
  });
  const jsonSchema = schemaHook.jsonSchema;
  // Resolve the loading gate in both modes: the full editor waits for the
  // schema AND the loaded artifact; the narrative-only editor needs only
  // metadata + the initialized incident store (no schema ever arrives, so
  // jsonSchema can't drive it). Set in both directions so a narrative-only→full
  // flip (after generating the full report) re-shows the skeleton until the
  // now-enabled schema loads. The incidentQuery.isSuccess check matters on that
  // flip: reportType is already set from the narrative phase, so the schema can
  // resolve before the full artifact arrives — without it the editor would
  // briefly render the schema form over stale narrative-built content.
  useEffect(() => {
    if (narrativeOnly) {
      setLoading(!(metadataQuery.isSuccess && incidentReady));
    } else {
      setLoading(!(jsonSchema && incidentQuery.isSuccess));
    }
  }, [
    narrativeOnly,
    metadataQuery.isSuccess,
    incidentReady,
    jsonSchema,
    incidentQuery.isSuccess,
  ]);
  useEffect(() => {
    if (!schemaHook.isError) return;
    // Without this a failed schema fetch left `loading` true forever and the
    // edit page showed its skeleton indefinitely. Mirror the hook's fatal
    // behavior: return home with an error toast.
    console.error("Failed to load report schema:", schemaHook.error);
    toast.error(
      (isAxiosError(schemaHook.error) &&
        schemaHook.error.response?.data?.error) ||
        "An error occurred while loading the report form. Please try again later.",
    );
    navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaHook.isError]);

  const reFetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;
    // Refetch metadata FIRST so narrativeOnly is re-derived (e.g. flips false
    // after full-report generation), then refetch the body query for the mode
    // the FRESH metadata reports. Sequencing matters on the flip: the closure's
    // narrativeOnly is still true at completion time, so a parallel refetch
    // would hit /report-narrative on a now-full report (the wrong endpoint,
    // which 404s). Deriving the mode from the refreshed metadata picks the
    // /artifact query instead.
    const { data } = await metadataQuery.refetch();
    const freshNarrativeOnly = data ? isNarrativeOnly(data) : narrativeOnly;
    await (freshNarrativeOnly
      ? narrativeQuery.refetch()
      : incidentQuery.refetch());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportID,
    narrativeOnly,
    metadataQuery.refetch,
    incidentQuery.refetch,
    narrativeQuery.refetch,
  ]);

  return {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    setLoading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    setEnableEditing,
    reFetchExtractedIncident,
    reportRms: rms,
    reportStatus,
    coComments,
    coReturnedAt,
    reportOwnerUserId,
    narrativeOnly,
  };
};
